import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';

// Mute types
export type MuteType = 'quick' | 'short' | 'extended' | 'permanent';

export const MUTE_DURATIONS: Record<MuteType, { label: string; hours: number | null }> = {
  quick: { label: '1 hour', hours: 1 },
  short: { label: '24 hours', hours: 24 },
  extended: { label: '7 days', hours: 168 },
  permanent: { label: 'Permanent', hours: null },
};

// Report categories
export type ReportCategory = 'spam' | 'harassment' | 'inappropriate' | 'other';

export const REPORT_CATEGORIES: Record<ReportCategory, string> = {
  spam: 'Spam',
  harassment: 'Harassment',
  inappropriate: 'Inappropriate Content',
  other: 'Other',
};

// Mute interface
export interface ChatMute {
  id: string;
  oderId: string;
  userId: string;
  username?: string;
  mutedBy: string;
  muteType: MuteType;
  reason: string | null;
  expiresAt: Date | null;
  createdAt: Date;
  isActive: boolean;
}

// Report interface
export interface ChatReport {
  id: string;
  leagueId: number;
  messageId: string;
  messageContent: string;
  messageAuthorId: string;
  messageAuthorUsername?: string;
  reportedBy: string;
  reporterUsername?: string;
  category: ReportCategory;
  description: string | null;
  status: 'pending' | 'dismissed' | 'actioned';
  resolvedBy: string | null;
  resolvedAt: Date | null;
  actionTaken: string | null;
  createdAt: Date;
}

interface MuteUserParams {
  leagueId: number;
  userId: string;
  muteType: MuteType;
  reason?: string;
}

interface UnmuteUserParams {
  leagueId: number;
  oderId: string;
}

interface ReportMessageParams {
  leagueId: number;
  messageId: string;
  messageContent: string;
  messageAuthorId: string;
  category: ReportCategory;
  description?: string;
}

interface ResolveReportParams {
  leagueId: number;
  reportId: string;
  status: 'dismissed' | 'actioned';
  actionTaken?: string;
  notes?: string;
}

/**
 * Hook to manage chat moderation (muting, reporting)
 */
export function useChatModeration(leagueId: number) {
  const queryClient = useQueryClient();

  // Get list of muted users
  const mutesQuery = useQuery<ChatMute[]>({
    queryKey: ['chat-mutes', leagueId],
    queryFn: async () => {
      const response = await apiRequest(`/api/leagues/${leagueId}/chat/mutes`);
      return response.map((mute: any) => ({
        ...mute,
        expiresAt: mute.expiresAt ? new Date(mute.expiresAt) : null,
        createdAt: new Date(mute.createdAt),
      }));
    },
    enabled: !!leagueId,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Get list of pending reports
  const reportsQuery = useQuery<ChatReport[]>({
    queryKey: ['chat-reports', leagueId],
    queryFn: async () => {
      const response = await apiRequest(`/api/leagues/${leagueId}/chat/reports?status=pending`);
      return response.map((report: any) => ({
        ...report,
        resolvedAt: report.resolvedAt ? new Date(report.resolvedAt) : null,
        createdAt: new Date(report.createdAt),
      }));
    },
    enabled: !!leagueId,
    staleTime: 30 * 1000,
  });

  // Mute a user
  const muteMutation = useMutation({
    mutationFn: async ({ leagueId, userId, muteType, reason }: MuteUserParams) => {
      return apiRequest(`/api/leagues/${leagueId}/chat/mute`, 'POST', {
        userId,
        muteType,
        reason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-mutes', leagueId] });
    },
  });

  // Unmute a user
  const unmuteMutation = useMutation({
    mutationFn: async ({ leagueId, oderId }: UnmuteUserParams) => {
      return apiRequest(`/api/leagues/${leagueId}/chat/mute/${oderId}`, 'DELETE');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-mutes', leagueId] });
    },
  });

  // Report a message
  const reportMutation = useMutation({
    mutationFn: async ({
      leagueId,
      messageId,
      messageContent,
      messageAuthorId,
      category,
      description,
    }: ReportMessageParams) => {
      return apiRequest(`/api/leagues/${leagueId}/chat/report`, 'POST', {
        messageId,
        messageContent,
        messageAuthorId,
        category,
        description,
      });
    },
  });

  // Resolve a report
  const resolveReportMutation = useMutation({
    mutationFn: async ({
      leagueId,
      reportId,
      status,
      actionTaken,
      notes,
    }: ResolveReportParams) => {
      return apiRequest(`/api/leagues/${leagueId}/chat/reports/${reportId}`, 'PATCH', {
        status,
        actionTaken,
        notes,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-reports', leagueId] });
    },
  });

  // Check if a user is muted
  const isUserMuted = (userId: string): boolean => {
    if (!mutesQuery.data) return false;

    const mute = mutesQuery.data.find(
      (m) => m.userId === userId && m.isActive
    );

    if (!mute) return false;

    // Check if mute has expired
    if (mute.expiresAt && new Date() > mute.expiresAt) {
      return false;
    }

    return true;
  };

  // Get mute info for a user
  const getUserMuteInfo = (userId: string): ChatMute | null => {
    if (!mutesQuery.data) return null;

    const mute = mutesQuery.data.find(
      (m) => m.userId === userId && m.isActive
    );

    if (!mute) return null;

    // Check if mute has expired
    if (mute.expiresAt && new Date() > mute.expiresAt) {
      return null;
    }

    return mute;
  };

  return {
    // Mutes
    mutes: mutesQuery.data || [],
    mutesLoading: mutesQuery.isLoading,
    mutesError: mutesQuery.error,
    muteUser: muteMutation.mutateAsync,
    isMuting: muteMutation.isPending,
    unmuteUser: unmuteMutation.mutateAsync,
    isUnmuting: unmuteMutation.isPending,
    isUserMuted,
    getUserMuteInfo,

    // Reports
    reports: reportsQuery.data || [],
    reportsLoading: reportsQuery.isLoading,
    reportsError: reportsQuery.error,
    pendingReportsCount: reportsQuery.data?.length || 0,
    reportMessage: reportMutation.mutateAsync,
    isReporting: reportMutation.isPending,
    resolveReport: resolveReportMutation.mutateAsync,
    isResolvingReport: resolveReportMutation.isPending,

    // Refetch functions
    refetchMutes: mutesQuery.refetch,
    refetchReports: reportsQuery.refetch,
  };
}
